import { Link } from 'react-router';
import { cn } from '@/lib/cn';
import type { Site } from '../api';

const badgeLabels: Record<string, string> = {
  exclusive: 'EXCLUSIVE',
  new: 'NEW',
  hot: 'HOT',
};

interface SiteTileProps {
  site: Site;
  /** Recorded with the click, so reports can tell the strip from the list. */
  placement: string;
  /** Whether this site has a live story behind its ring. */
  hasStory: boolean;
}

/**
 * One round tile in the featured strip.
 *
 * What it does when tapped depends on whether there is a story: a lit ring opens
 * the story, a plain tile goes straight to the partner. Sending someone to an
 * affiliate site when the ring told them there was something to watch would be a
 * bait-and-switch, and it is the reason this is two different elements rather
 * than one anchor with a conditional href.
 *
 * The ring colour comes from the database, so it is an inline style — Tailwind
 * cannot generate a class for a value it has never seen.
 */
export function SiteTile({ site, placement, hasStory }: SiteTileProps) {
  const badge = badgeLabels[site.badge];

  const inner = (
    <>
      <span className="relative block">
        <span
          className={cn('block rounded-full p-[2px] transition group-hover:scale-105')}
          style={{
            background: hasStory
              ? // A full sweep reads as "unwatched", the way every story UI does.
                `conic-gradient(from 210deg, ${site.brandColor}, #f5c542, ${site.brandColor})`
              : `linear-gradient(160deg, ${site.brandColor}55, transparent 70%)`,
          }}
        >
          <span className="block rounded-full bg-surface-950 p-1">
            {site.iconUrl ? (
              <img
                src={site.iconUrl}
                alt=""
                width={72}
                height={72}
                loading="lazy"
                className="size-[62px] rounded-full object-cover sm:size-[72px]"
              />
            ) : (
              <span className="grid size-[62px] place-items-center rounded-full bg-surface-800 text-lg font-bold text-surface-400 sm:size-[72px]">
                {site.name.slice(0, 1)}
              </span>
            )}
          </span>
        </span>

        {badge ? (
          <span
            className={cn(
              'absolute -top-1 left-1/2 -translate-x-1/2 rounded-sm px-1.5 py-0.5 text-[9px] font-bold tracking-wide',
              site.badge === 'exclusive'
                ? 'bg-gold-gradient text-surface-950'
                : 'bg-live-500 text-white',
            )}
          >
            {badge}
          </span>
        ) : null}
      </span>

      <span className="line-clamp-1 text-center text-xs text-surface-300 group-hover:text-surface-100">
        {site.name}
      </span>
    </>
  );

  const className = 'group flex w-20 shrink-0 flex-col items-center gap-2 sm:w-24';

  if (hasStory) {
    return (
      <Link
        to={`/stories/${site.slug}`}
        className={className}
        aria-label={`${site.name} hikâyesini aç`}
      >
        {inner}
      </Link>
    );
  }

  return (
    <a
      href={`${site.clickUrl}?from=${encodeURIComponent(placement)}`}
      target="_blank"
      // noopener is the one that matters: without it the opened page gets a
      // handle on this window and can navigate it somewhere else.
      rel="noreferrer noopener sponsored"
      className={className}
    >
      {inner}
    </a>
  );
}
